import React, { useState, useRef, useEffect } from 'react';

// UI Lib dependencies
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import dayjs from 'dayjs';

// UI Local Lib dependencies
import DataGrid from 'ui/components/DataGrid';
import Input from 'ui/components/Input';
import Switch from 'ui/components/Switch';
import DeleteModal from '../../../DeleteModal';

// Hooks

import useCompany from 'hooks/usecase/useCompany';
import useScopes from 'hooks/usecase/useScopes';

// Configs
import { tableSchemaBase as columns } from './Scopes.config';

// Validation
import validation from './validators/company.validator';

// Icons
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

import { useParams } from 'react-router-dom';

// Global State
import useStore from 'store';
import { set } from 'immutable';
import { useTranslation } from 'react-i18next';

const Scopes = () => {
  const { t } = useTranslation();

  // States
  const [queryParams, setQueryParams] = useState({ page: 1, size: 5 });
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  let { companyId } = useParams();
  // Hook
  const {
    scopes,
    control,
    isLoading,
    onRemoveScope,
    onAddScope,
    isOpenAddModal,
    setIsOpenAddModal,
    reset,
    isOpenEditModal,
    setIsOpenEditModal,
    setEditingScope,
    onEditScope,
  } = useScopes(companyId);

  const {
    completScopes,
    scopes: scopesState,
    setScopes,
    currentPage,
    size,
    setSize,
    setCurrentPage,
    totalPages,
    setCompleteScopes,
    setTotalPages,
    setAllScopes,
    allScopes,
  } = useStore();

  // Refs
  const refName = useRef();

  // Helper
  const updateList = (companies) =>
    companies.reduce((acc, currentItem) => {
      if (currentItem.companyId === companyId) {
        const modifiedItem = {
          ...currentItem,
          createdAt: dayjs(currentItem.createdAt).format('DD-MM-YYYY'),
        };
        acc.push(modifiedItem);
      }

      return acc;
    }, []);

  function getPaginatedItems(items, pageSize, pageNumber) {
    let filteredItems = items;
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filteredItems.slice(startIndex, endIndex);
    return paginatedItems;
  }

  function calculateTotalPages(items, itemsPerPage) {
    // Vérifier si les paramètres sont valides
    if (!Array.isArray(items) || itemsPerPage < 1) {
      return 0;
    }

    // Calculer le nombre total de pages
    const totalPages = Math.ceil(items.length / itemsPerPage);

    return totalPages;
  }

  const handleOnFilter = (filters, currentPage, currentSize) => {
    let queryParamsFilter = {};
    if (filters.items) {
      filters.items.map((filter) => {
        switch (filter.field) {
          case 'scopeName':
            queryParamsFilter = { ...queryParamsFilter, userScope: filter.value };
            break;
          case 'user':
            queryParamsFilter = { ...queryParamsFilter, name: filter.value };
            break;
          case 'registrationDate':
            queryParamsFilter = {
              ...queryParamsFilter,
              createdAt: filter.value && dayjs(filter.value).format('YYYY-MM-DD'),
            };
            break;
          default:
            queryParamsFilter = { ...queryParamsFilter, [filter.field]: filter.value };
            break;
        }
      });
    }
    // zustand : Filter using state management
    if (!queryParamsFilter.name) {
      setCompleteScopes(allScopes, size);
      setScopes(allScopes.slice(0, size));
      setTotalPages(calculateTotalPages(allScopes, size));
    } else {
      const filteredItems = allScopes.filter(
        (item) =>
          item.name
            .toLowerCase()
            .includes(
              queryParamsFilter.name && queryParamsFilter.name !== undefined ? queryParamsFilter.name.toLowerCase() : ''
            ) && item.companyId === companyId
      );
      const startIndex = (currentPage - 1) * size;
      const endIndex = startIndex + size;
      const paginatedItems = filteredItems.slice(startIndex, endIndex);
      setTotalPages(calculateTotalPages(filteredItems, size));
      setScopes(paginatedItems);
      setCompleteScopes(filteredItems, size);
    }
  };

  const handleOnSort = (sort, currentPage, currentSize) => {
    let queryParamsSort = {};
    switch (sort.orderBy) {
      case 'scopeName':
        queryParamsSort = { ...sort, orderBy: 'userScope' };
        break;
      case 'user':
        queryParamsSort = { ...sort, orderBy: 'firstname' };
        break;
      case 'registrationDate':
        queryParamsSort = { ...sort, orderBy: 'createdAt' };
        break;
      case '':
        queryParamsSort = { orderBy: '' };
        break;
      default:
        queryParamsSort = { ...sort };
        break;
    }
    setQueryParams({ ...queryParams, page: currentPage, size: currentSize, ...queryParamsSort });
  };

  const handleOpenModal = () => {
    setIsOpenAddModal(true);
  };

  const handleCloseModal = () => {
    setIsOpenAddModal(false);
    reset();
  };

  const handleCloseEditModal = () => {
    setIsOpenEditModal(false);
    reset({ name: '' });
  };

  // zustand : Set state management items
  useEffect(() => {
    if (scopes) {
      setScopes(updateList(scopes).slice(0, 10));
      setCompleteScopes(updateList(scopes), size);
      setAllScopes(updateList(scopes), size);
    }
  }, [scopes]);

  // zustand : Reset stat management page size
  useEffect(() => {
    return () => {
      setSize(10);
      setCurrentPage(1);
    };
  }, []);

  return (
    <Stack sx={{ minHeight: '630px', backgroundColor: 'white', paddingLeft: '90px' }}>
      <Stack sx={{ paddingBottom: '9px' }}>
        <Button
          endIcon={<AddIcon />}
          onClick={handleOpenModal}
          sx={{
            width: '114px',
            height: '50px',
            borderRadius: '64px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'primary.main',
            color: 'white',
            fontWeight: 500,
            fontSize: '15px',
            boxShadow: '0px 3px 5px -1px #00000033',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}>
          {t('add')}
        </Button>
      </Stack>
      {/** DataGrid */}
      <DataGrid
        columns={columns}
        rows={scopes ? scopesState : []}
        onDelete={(item) => {
          setSelectedItem(item);
          setIsOpenDeleteModal(true);
        }}
        onShow={() => {}}
        onEdit={(scope) => {
          setEditingScope(scope);
        }}
        onRowClicked={(scope, event) => {
          if (!event.defaultMuiPrevented) {
            setEditingScope(scope.row);
          }
        }}
        onFilter={handleOnFilter}
        onSort={handleOnSort}
        totalItems={scopes ? updateList(completScopes).length : 10}
        totalPages={totalPages ? totalPages : 1}
        onPaginationChange={(page, nbrPages) => {
          setSize(nbrPages);
          setScopes(getPaginatedItems(completScopes, nbrPages, page));
          setTotalPages(calculateTotalPages(completScopes, nbrPages));
        }}
        loading={isLoading}
      />
      {/* Add modal */}
      <Dialog open={isOpenAddModal} onClose={handleCloseModal}>
        <DialogTitle>
          <Typography
            style={{
              color: '#000000DE',
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: '32px',
            }}>
            {'Create a new scope'}
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              '-webkit-appearance': 'none',
              width: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '25px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '-webkit-box-shadow': '0 0 1px rgba(255, 255, 255, 0.5)',
            },
          }}>
          <IconButton
            aria-label="close"
            onClick={handleCloseModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}>
            <CloseIcon />
          </IconButton>
          <Box sx={{ padding: '8px 24px', height: '70px', minWidth: '300px' }}>
            <Input
              ref={refName}
              control={control}
              variant="outlined"
              label="Name"
              name="name"
              rules={validation.name}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '19px 77px',
            borderTop: '1px solid #dfe0e0',
          }}>
          <Button variant="outlined" onClick={handleCloseModal} size="large">
            {'BACK'}
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: 'primary.dark' }}
            onClick={() => {
              onAddScope();
            }}
            size="large">
            {'SAVE'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={isOpenEditModal} onClose={handleCloseEditModal}>
        <DialogTitle>
          <Typography
            style={{
              color: '#000000DE',
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: '32px',
            }}>
            {'Edit scope'}
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              '-webkit-appearance': 'none',
              width: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '25px',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '-webkit-box-shadow': '0 0 1px rgba(255, 255, 255, 0.5)',
            },
          }}>
          <IconButton
            aria-label="close"
            onClick={() => setIsOpenEditModal(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}>
            <CloseIcon />
          </IconButton>
          <Box sx={{ padding: '8px 24px', height: '70px', minWidth: '300px' }}>
            <Input
              ref={refName}
              control={control}
              variant="outlined"
              label="Name"
              name="name"
              rules={validation.name}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '19px 77px',
            borderTop: '1px solid #dfe0e0',
          }}>
          <Button variant="outlined" onClick={handleCloseEditModal} size="large">
            {'BACK'}
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: 'primary.dark' }}
            onClick={() => {
              onEditScope();
            }}
            size="large">
            {'SAVE'}
          </Button>
        </DialogActions>
      </Dialog>
      <DeleteModal
        title="Delete Scope"
        description="Are you sure you want to delete this scope"
        open={isOpenDeleteModal}
        onDelete={() => {
          onRemoveScope(selectedItem.id);
        }}
        handelModal={() => {
          setIsOpenDeleteModal(false);
        }}
      />
    </Stack>
  );
};

export default Scopes;
